import { getter } from '@/lib/utils/object';
import { formatUrl } from '@/lib/utils/string';
import { IFormInputs } from '.';
import { User } from '@/redux/apis/frontend-api';
import {
  CARRIER_SIZES,
  CARRIER_TYPES,
  STORE_COMPONENTS,
} from '@/lib/constants';

type ValueOption = {
  value?: string;
};

function serializeUrl(data: IFormInputs): string {
  const targetUrl = data.targetUrl;

  return typeof targetUrl === 'string' && targetUrl.length > 0
    ? formatUrl(targetUrl)
    : '';
}

function serializeCommunicationPurpose(data: IFormInputs): string | undefined {
  return data.communicationPurpose;
}

function serializeSubcategory(data: IFormInputs): string | undefined {
  return data.subcategory;
}

function serializeArray(
  array: ValueOption[] | undefined,
  sortingRef?: ValueOption[]
): string[] {
  if (!array || !array.length) return [];

  const serializedData = array
    .map((item) => item.value)
    .filter((value): value is string => typeof value === 'string');

  if (sortingRef) {
    const sortedKeys = sortingRef
      .map((item) => item.value)
      .filter((value): value is string => typeof value === 'string');

    serializedData.sort(
      (a: string, b: string) => sortedKeys.indexOf(a) - sortedKeys.indexOf(b)
    );
  }
  return serializedData;
}

function getDepartments() {
  return STORE_COMPONENTS.map((component) => component.departments).flat();
}

export function serializeUser({
  username,
  cdsLocation,
  country,
  jobTitle,
  displayName,
}: User) {
  return {
    email: username || '',
    location: cdsLocation,
    country: country.toLowerCase(),
    role: jobTitle,
    name: displayName,
  };
}

export function serializePermissions(
  data: IFormInputs,
  user: User | undefined
) {
  const { permissions: permissionLevel } = data;
  if (!permissionLevel || permissionLevel === 'public') return undefined;
  // If the user is not found: do not allow to make it private
  if (!user && permissionLevel === 'private') {
    throw new Error(
      `Cannot serialize permissions for code with permission level "${permissionLevel}" without a user`
    );
  }
  // Grant permissions to user and manager
  const permissions: { type: 'user' | 'location'; value: string }[] = [];
  if (user) {
    permissions.push({
      type: 'user',
      value: user.username,
    });
    if (user.manager) {
      permissions.push({
        type: 'user',
        value: user.manager,
      });
    }
  }
  // If location level permission is selected, grant permissions to user's location
  if (permissionLevel === 'location') {
    if (user?.cdsLocation) {
      permissions.push({
        type: 'location',
        value: user.cdsLocation,
      });
    }
    const locationSite = data.location?.site;
    // If the code is created for a different location, grant permissions to this location as well
    if (locationSite && user?.cdsLocation !== locationSite) {
      permissions.push({
        type: 'location',
        value: locationSite,
      });
    }
  }
  return permissions;
}

export function serializeForm(data: IFormInputs, user: User | undefined) {
  const serializedData = {
    // target group
    targetGroup: getter(data, 'targetGroup') || '',
    // communication purpose
    communicationPurpose: serializeCommunicationPurpose(data),
    // subcategory
    subcategory: serializeSubcategory(data),
    // Code settings
    targetUrl: serializeUrl(data),
    codeName: getter(data, 'codeName'),
    icon: data.showIcon ? getter(data, 'icon') || '' : '',
    // Location
    location: {
      country: getter(data, 'location.country'),
      site: getter(data, 'location.site'),
    },
    onlyOnlineUsage: getter(data, 'onlyOnlineUsage'),
    store: {
      component: serializeArray(data.store?.component, STORE_COMPONENTS),
      department: serializeArray(data.store?.department, getDepartments()),
    },
    // Code permissions
    permissions: serializePermissions(data, user),
    // Communication carrier
    carrier: {
      type: serializeArray(data.carrier?.type, CARRIER_TYPES),
      size: serializeArray(data.carrier?.size, CARRIER_SIZES),
    },
  };
  return serializedData;
}
